import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
  alpha,
  useTheme,
  Divider,
} from "@mui/material";
import {
  Plus,
  Trash2,
  Edit2,
  X,
  Target,
  Copy,
  Check,
  Search,
  Zap,
  Users,
} from "lucide-react";
import {
  fetchLIICPs,
  createLIICP,
  updateLIICP,
  deleteLIICP,
  generateLIICPFormulas,
  runLIICPDiscovery,
} from "../../../../../../redux/actions/linkedinOutreachAction";

const EMPTY_FORM = {
  name: "",
  description: "",
  target_country: "",
  criteria: {
    job_titles: [],
    industries: [],
    locations: [],
    company_sizes: [],
    seniority: [],
    keywords: [],
    exclusions: [],
  },
};

const criteriaToStr = (arr) => (arr || []).join(", ");
const strToCriteria = (str) =>
  str
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const ICPList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { icps, loading } = useSelector((state) => state.linkedinOutreach);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingICP, setEditingICP] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Formulas state
  const [formulasDialogOpen, setFormulasDialogOpen] = useState(false);
  const [formulasICP, setFormulasICP] = useState(null);
  const [formulas, setFormulas] = useState([]);
  const [generatingFormulas, setGeneratingFormulas] = useState(false);
  const [editedFormulas, setEditedFormulas] = useState([]);
  const [copiedIdx, setCopiedIdx] = useState(null);

  // Discovery state
  const [discoveryResult, setDiscoveryResult] = useState(null);
  const [runningDiscovery, setRunningDiscovery] = useState(null);

  useEffect(() => {
    dispatch(fetchLIICPs());
  }, [dispatch]);

  const openCreate = () => {
    setEditingICP(null);
    setFormData(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (icp) => {
    setEditingICP(icp);
    setFormData({
      name: icp.name || "",
      description: icp.description || "",
      target_country: icp.target_country || "",
      criteria: {
        job_titles: icp.criteria?.job_titles || [],
        industries: icp.criteria?.industries || [],
        locations: icp.criteria?.locations || [],
        company_sizes: icp.criteria?.company_sizes || [],
        seniority: icp.criteria?.seniority || [],
        keywords: icp.criteria?.keywords || [],
        exclusions: icp.criteria?.exclusions || [],
      },
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingICP) {
        await dispatch(updateLIICP(editingICP.id, formData));
      } else {
        await dispatch(createLIICP(formData));
      }
      setDialogOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await dispatch(deleteLIICP(deleteConfirm.id));
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleGenerateFormulas = async (icp) => {
    setFormulasICP(icp);
    setFormulas(icp.search_formulas || []);
    setEditedFormulas(icp.search_formulas || []);
    setFormulasDialogOpen(true);
  };

  const handleRegenerateFormulas = async () => {
    if (!formulasICP) return;
    setGeneratingFormulas(true);
    try {
      const result = await dispatch(generateLIICPFormulas(formulasICP.id));
      const newFormulas = result?.search_formulas || result?.formulas || [];
      setFormulas(newFormulas);
      setEditedFormulas(newFormulas);
    } finally {
      setGeneratingFormulas(false);
    }
  };

  const handleCopyFormula = (formula, idx) => {
    navigator.clipboard.writeText(formula);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const handleFormulaEdit = (idx, value) => {
    const updated = [...editedFormulas];
    updated[idx] = value;
    setEditedFormulas(updated);
  };

  const handleSaveFormulas = async () => {
    if (!formulasICP) return;
    setSaving(true);
    try {
      await dispatch(
        updateLIICP(formulasICP.id, { search_formulas: editedFormulas })
      );
      setFormulasDialogOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleRunDiscovery = async (icp) => {
    setRunningDiscovery(icp.id);
    setDiscoveryResult(null);
    try {
      const result = await dispatch(runLIICPDiscovery(icp.id));
      setDiscoveryResult({ icpId: icp.id, ...result });
    } finally {
      setRunningDiscovery(null);
    }
  };

  const setCriteriaField = (field, value) => {
    setFormData({
      ...formData,
      criteria: {
        ...formData.criteria,
        [field]: strToCriteria(value),
      },
    });
  };

  const criteriaFields = [
    { key: "job_titles", label: "Job Titles" },
    { key: "industries", label: "Industries" },
    { key: "locations", label: "Locations" },
    { key: "company_sizes", label: "Company Sizes" },
    { key: "seniority", label: "Seniority Levels" },
    { key: "keywords", label: "Keywords" },
    { key: "exclusions", label: "Exclusions (competitors)" },
  ];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          Ideal Customer Profiles
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<Plus size={16} />}
          onClick={openCreate}
          sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
        >
          New ICP
        </Button>
      </Box>

      {/* Card Grid */}
      {loading && (icps || []).length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : (icps || []).length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
          }}
        >
          <Target
            size={40}
            color={theme.palette.text.secondary}
            style={{ opacity: 0.4, marginBottom: 12 }}
          />
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary }}
          >
            No ICPs yet. Create one to start discovering leads.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
            gap: 2,
            overflow: "auto",
            flex: 1,
            minHeight: 0,
          }}
        >
          {(icps || []).map((icp) => (
            <Paper
              key={icp.id}
              elevation={0}
              sx={{
                borderRadius: "12px",
                border: `1px solid ${theme.palette.divider}`,
                p: 2.5,
                cursor: "pointer",
                transition: "border-color 0.15s, box-shadow 0.15s",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                },
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
              onClick={() => openEdit(icp)}
            >
              {/* Card Header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      lineHeight: 1.3,
                    }}
                  >
                    {icp.name}
                  </Typography>
                  {icp.description && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "0.8rem",
                        mt: 0.5,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {icp.description}
                    </Typography>
                  )}
                  {icp.target_country && (
                    <Chip
                      label={icp.target_country}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5, fontSize: "0.65rem", height: 18, borderRadius: "4px" }}
                    />
                  )}
                </Box>
                <Box
                  sx={{ display: "flex", gap: 0.5, ml: 1 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Chip
                    label={icp.is_active ? "Active" : "Inactive"}
                    size="small"
                    color={icp.is_active ? "success" : "default"}
                    sx={{
                      borderRadius: "6px",
                      fontSize: "0.65rem",
                      height: 20,
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Box>

              {/* Lead Count */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Users size={14} color={theme.palette.text.secondary} />
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.8rem",
                  }}
                >
                  {icp.lead_count || 0} leads
                </Typography>
              </Box>

              {/* Criteria Chips */}
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {(icp.criteria?.job_titles || []).slice(0, 3).map((jt, i) => (
                  <Chip
                    key={`jt-${i}`}
                    label={jt}
                    size="small"
                    sx={{
                      borderRadius: "6px",
                      fontSize: "0.6rem",
                      height: 18,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                    }}
                  />
                ))}
                {(icp.criteria?.locations || []).slice(0, 2).map((loc, i) => (
                  <Chip
                    key={`loc-${i}`}
                    label={loc}
                    size="small"
                    sx={{
                      borderRadius: "6px",
                      fontSize: "0.6rem",
                      height: 18,
                      bgcolor: alpha("#10b981", 0.1),
                      color: "#10b981",
                    }}
                  />
                ))}
                {(icp.criteria?.industries || []).slice(0, 2).map((ind, i) => (
                  <Chip
                    key={`ind-${i}`}
                    label={ind}
                    size="small"
                    sx={{
                      borderRadius: "6px",
                      fontSize: "0.6rem",
                      height: 18,
                      bgcolor: alpha("#f59e0b", 0.1),
                      color: "#f59e0b",
                    }}
                  />
                ))}
                {(() => {
                  const totalExtra =
                    Math.max(0, (icp.criteria?.job_titles || []).length - 3) +
                    Math.max(0, (icp.criteria?.locations || []).length - 2) +
                    Math.max(0, (icp.criteria?.industries || []).length - 2);
                  return totalExtra > 0 ? (
                    <Chip
                      label={`+${totalExtra}`}
                      size="small"
                      sx={{
                        borderRadius: "6px",
                        fontSize: "0.6rem",
                        height: 18,
                        bgcolor: alpha(theme.palette.divider, 0.5),
                        color: theme.palette.text.secondary,
                      }}
                    />
                  ) : null;
                })()}
              </Box>

              {/* Action Buttons */}
              <Divider sx={{ my: 0.5 }} />
              <Box
                sx={{ display: "flex", gap: 1 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  size="small"
                  startIcon={
                    generatingFormulas ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <Zap size={14} />
                    )
                  }
                  onClick={() => handleGenerateFormulas(icp)}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    borderRadius: "6px",
                  }}
                >
                  Formulas
                </Button>
                <Button
                  size="small"
                  startIcon={
                    runningDiscovery === icp.id ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <Search size={14} />
                    )
                  }
                  onClick={() => handleRunDiscovery(icp)}
                  disabled={runningDiscovery === icp.id}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    borderRadius: "6px",
                  }}
                >
                  Run Discovery
                </Button>
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => setDeleteConfirm(icp)}
                  >
                    <Trash2 size={14} color={theme.palette.error?.main} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Discovery Result */}
              {discoveryResult && discoveryResult.icpId === icp.id && (
                <Box
                  sx={{
                    bgcolor: alpha("#10b981", 0.08),
                    borderRadius: "8px",
                    p: 1.5,
                    display: "flex",
                    gap: 2,
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, fontSize: "1rem", color: "#10b981" }}
                    >
                      {discoveryResult.total_found ?? 0}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "0.6rem",
                      }}
                    >
                      Found
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: "1rem",
                        color: theme.palette.primary.main,
                      }}
                    >
                      {discoveryResult.new_leads ?? 0}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "0.6rem",
                      }}
                    >
                      New
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: "1rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {discoveryResult.duplicates ?? 0}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "0.6rem",
                      }}
                    >
                      Duplicates
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          ))}
        </Box>
      )}

      {/* Create / Edit ICP Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {editingICP ? "Edit ICP" : "New ICP"}
          <IconButton onClick={() => setDialogOpen(false)} size="small">
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
          >
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              fullWidth
              size="small"
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              fullWidth
              size="small"
              multiline
              rows={2}
            />
            <TextField
              label="Target Country"
              value={formData.target_country}
              onChange={(e) =>
                setFormData({ ...formData, target_country: e.target.value })
              }
              fullWidth
              size="small"
              placeholder="e.g. Morocco, UAE"
            />
            <Divider sx={{ my: 0.5 }} />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, fontSize: "0.85rem" }}
            >
              Criteria
            </Typography>
            {criteriaFields.map(({ key, label }) => (
              <TextField
                key={key}
                label={label}
                value={criteriaToStr(formData.criteria[key])}
                onChange={(e) => setCriteriaField(key, e.target.value)}
                fullWidth
                size="small"
                helperText="Comma-separated values"
                FormHelperTextProps={{
                  sx: { fontSize: "0.65rem", mt: 0.3 },
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.name || saving}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {saving ? "Saving..." : editingICP ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Search Formulas Dialog */}
      <Dialog
        open={formulasDialogOpen}
        onClose={() => setFormulasDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Search Formulas — {formulasICP?.name}
          <IconButton
            onClick={() => setFormulasDialogOpen(false)}
            size="small"
          >
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
          >
            <Box
              sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  generatingFormulas ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <Zap size={14} />
                  )
                }
                onClick={handleRegenerateFormulas}
                disabled={generatingFormulas}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {generatingFormulas
                  ? "Generating..."
                  : "Generate Search Formulas"}
              </Button>
            </Box>

            {editedFormulas.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  color: theme.palette.text.secondary,
                }}
              >
                <Typography variant="body2">
                  No formulas yet. Click "Generate Search Formulas" to create
                  Google/LinkedIn search queries from this ICP's criteria.
                </Typography>
              </Box>
            ) : (
              editedFormulas.map((formula, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                  }}
                >
                  <TextField
                    value={formula}
                    onChange={(e) => handleFormulaEdit(idx, e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    sx={{
                      "& .MuiInputBase-root": {
                        fontSize: "0.85rem",
                        fontFamily: "monospace",
                      },
                    }}
                  />
                  <Tooltip
                    title={copiedIdx === idx ? "Copied!" : "Copy"}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleCopyFormula(formula, idx)}
                      sx={{ mt: 0.5 }}
                    >
                      {copiedIdx === idx ? (
                        <Check size={14} color="#10b981" />
                      ) : (
                        <Copy size={14} />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const updated = editedFormulas.filter(
                          (_, i) => i !== idx
                        );
                        setEditedFormulas(updated);
                      }}
                      sx={{ mt: 0.5 }}
                    >
                      <Trash2
                        size={14}
                        color={theme.palette.error?.main}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))
            )}

            {editedFormulas.length > 0 && (
              <Button
                size="small"
                startIcon={<Plus size={14} />}
                onClick={() => setEditedFormulas([...editedFormulas, ""])}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  alignSelf: "flex-start",
                  borderRadius: "6px",
                }}
              >
                Add Formula
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormulasDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveFormulas}
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {saving ? "Saving..." : "Save Formulas"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Delete ICP</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete{" "}
            <strong>{deleteConfirm?.name}</strong>? This will not delete
            associated leads.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={
              deleting ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ICPList;
